import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter, useRoutes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { store, persistor } from './store';
import { routes } from './routes';
import theme from './theme';
import { installSessionExpiryHandler, registerPersistor } from './utils/sessionExpiry';
import './brandColors.css';
import './styles.css';

// Registered before anything renders, so an expired token sends the student to sign in
// rather than surfacing as a request error on whichever screen made the call.
installSessionExpiryHandler();
registerPersistor(persistor);

// Mobile-specific loading component
const MobileLoadingComponent = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #8b5cf6 100%)',
      color: 'white'
    }}
  >
    <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
    <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
      Loading LiveOlympiad...
    </Typography>
    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, textAlign: 'center' }}>
      Please wait while we prepare your learning experience
    </Typography>
  </Box>
);

// Mobile-specific error boundary
class MobileErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error; isAuthError?: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    // Check if this is an authentication error
    const isAuthError = error.message.includes('401') || 
                       error.message.includes('Unauthorized') ||
                       error.message.includes('authentication');
    
    return { hasError: true, error, isAuthError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
  }

  handleLogout = () => {
    // Clear all authentication data comprehensively
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('auth_error');
    localStorage.removeItem('lastStartedTest');
    localStorage.removeItem('lastTestStartTime');
    localStorage.removeItem('selectedTest');
    localStorage.removeItem('persist:root');
    sessionStorage.clear();

    // Clear any app-specific cached data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('liveolympiad_') || key.startsWith('student_') || key.startsWith('test_')) {
        localStorage.removeItem(key);
      }
    });

    // Clear WebView cache if in mobile app
    if (window.location.hostname === '10.0.0.10') {
      try {
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
        }
      } catch (error) {
      }
    }

    // Reload the page to reset the application state
    window.location.href = '/login';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '100vh',
            padding: 2,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 50%, #8b5cf6 100%)',
            color: 'white'
          }}
        >
          {this.state.isAuthError ? (
            <>
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3, 
                  maxWidth: '400px',
                  '& .MuiAlert-icon': { color: 'white' },
                  '& .MuiAlert-message': { color: 'white' }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Session Expired
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your login session has expired. Please log in again to continue.
                </Typography>
              </Alert>
              
              <Button 
                onClick={this.handleLogout}
                variant="contained"
                size="large"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  minHeight: '48px',
                  minWidth: '120px',
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Oops! Something went wrong
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                We're sorry, but there was an error loading the application.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button 
                  onClick={this.handleReload} 
                  variant="contained"
                  size="large"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    minHeight: '48px',
                    minWidth: '120px',
                    fontSize: '16px',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                    }
                  }}
                >
                  Reload Page
                </Button>
                
                <Button 
                  onClick={this.handleLogout}
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    minHeight: '48px',
                    minWidth: '120px',
                    fontSize: '16px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Logout
                </Button>
              </Box>
            </>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

const AppRoutes = () => {
  const element = useRoutes(routes);
  return element;
};

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <MobileErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={<MobileLoadingComponent />} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </MobileErrorBoundary>
  </React.StrictMode>
);